/**
 * Safe error message extraction for API errors.
 * Handles Axios errors, standard Error objects, and unknown types.
 */

function humanizeValidationItem(
  item: { loc?: unknown[]; msg?: string; type?: string; ctx?: { min_length?: number } },
  labels: Record<string, string>
): string {
  if (!item || typeof item !== 'object') return '';
  const loc = Array.isArray(item.loc)
    ? item.loc.filter((part) => part !== 'body' && typeof part === 'string')
    : [];
  const key = String(loc[loc.length - 1] || '');
  const label = labels[key] || key;
  const type = String(item.type || '');
  const min = item.ctx?.min_length;

  if (type.includes('email') || /not a valid email/i.test(String(item.msg || ''))) {
    return `${label || 'Email'} should look like you@clinic.org.`;
  }
  if (type.includes('too_short') || /at least \d+ character/i.test(String(item.msg || ''))) {
    if (key === 'name') return `Clinic name needs at least ${min || 2} characters.`;
    if (key === 'admin_email' || key === 'email') {
      return `${label || 'Email'} should look like you@clinic.org.`;
    }
    if (key === 'password') return `Password needs at least ${min || 8} characters.`;
    if (key === 'invite_code') return 'Paste the full invite code from your clinic admin.';
    return `${label || 'This field'} is too short.`;
  }

  const msg = String(item.msg || '').replace(/^Value error, /i, '');
  if (label && msg) return `${label}: ${msg}`;
  return msg;
}

export function getErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    const axiosErr = err as any;
    if (axiosErr?.response?.data) {
      const data = axiosErr.response.data;

      if (data.detail) {
        if (typeof data.detail === 'string') {
          return data.detail;
        }
        if (Array.isArray(data.detail)) {
          const labels = {
            name: 'Clinic name',
            admin_name: 'Your name',
            admin_email: 'Work email',
            email: 'Email',
            password: 'Password',
            invite_code: 'Invite code',
          };
          const parts = data.detail
            .map((item: any) => humanizeValidationItem(item, labels))
            .filter(Boolean);
          if (parts.length) return parts.join(' ');
        }
        if (typeof data.detail === 'object' && data.detail.message) {
          return data.detail.message;
        }
      }

      if (typeof data.message === 'string') {
        return data.message;
      }

      if (typeof data.error === 'string') {
        return data.error;
      }
    }

    return err.message;
  }

  if (typeof err === 'string') {
    return err;
  }

  try {
    return JSON.stringify(err);
  } catch {
    return 'Unknown error';
  }
}

export function getErrorCode(err: unknown): string | null {
  if (err instanceof Error) {
    const axiosErr = err as any;
    if (axiosErr?.response?.data) {
      const data = axiosErr.response.data;

      if (data.detail && typeof data.detail === 'object') {
        if (data.detail.error_code) {
          return data.detail.error_code;
        }
        if (data.detail.code) {
          return data.detail.code;
        }
      }

      if (data.error_code) {
        return data.error_code;
      }

      if (data.code) {
        return data.code;
      }
    }
  }

  return null;
}

export function getErrorPurpose(err: unknown): string | null {
  if (err instanceof Error) {
    const axiosErr = err as any;
    if (axiosErr?.response?.data) {
      const data = axiosErr.response.data;

      if (data.detail && typeof data.detail === 'object' && data.detail.purpose) {
        return data.detail.purpose;
      }

      if (data.purpose) {
        return data.purpose;
      }
    }
  }

  return null;
}
